import * as THREE from "three"
import { Canvas, useFrame, useThree } from "@react-three/fiber"
import { Environment, useTexture } from "@react-three/drei"
import { Physics, useSphere } from "@react-three/cannon"
import { EffectComposer, N8AO, SMAA, Bloom } from "@react-three/postprocessing"
import { useMemo } from "react"
import LinkGMV3D from "./Banner500px.png"

const rfs = THREE.MathUtils.randFloatSpread
const sphereGeometry = new THREE.SphereGeometry(1, 32, 32)

export const App = () => (
    <>
        <div style={{ position: "absolute", top: 20, left: 20, color: "white", zIndex: 1 }}>
            <a href="https://www.gamutviewer.com" target="_blank" rel="noreferrer" style={{ color: "white" }}>
                <img src={LinkGMV3D} alt="500px Banner" style={{ width: '200px' }} />
            </a>
        </div>
        <div style={{ position: "absolute", bottom: 20, left: 20, color: "white", zIndex: 1 }}>
            <a href="https://www.gamutviewer.com" target="_blank" rel="noreferrer" style={{ color: "white" }}>
                Gamutviewer.com
            </a>
        </div>
        <Canvas shadows gl={{ antialias: false }} dpr={[1, 1.5]} camera={{ position: [0, 0, 20], fov: 35, near: 1, far: 40 }} >
            <ambientLight intensity={0.5} />
            <color attach="background" args={["black"]} />
            <spotLight intensity={1} angle={0.2} penumbra={1} position={[30, 30, 30]} castShadow shadow-mapSize={[512, 512]} />
            <Physics gravity={[0, 2, 0]} iterations={10}>
                <Pointer />
                <Clump />
            </Physics>
            <Environment files="/adamsbridge.hdr" />
            <EffectComposer disableNormalPass multisampling={0}>
                <N8AO halfRes color="black" aoRadius={2} intensity={1} aoSamples={6} denoiseSamples={4} />
                <Bloom mipmapBlur levels={7} intensity={1} />
                <SMAA />
            </EffectComposer>
        </Canvas>
    </>
)

function Clump() {
    //const textures = useTexture(["/cross.jpg", "/cross_CMM.jpg", "/cross_POP.jpg", "/cross_JUP.jpg", "/cross.jpg", "/cross_JUP.jpg"])
    const textures = useTexture(["/Ballon_GMV3D.png", "/cross_JUP.jpg", "/Ballon_GMV3D.png", "/cross_CMM.jpg", "/Ballon_GMV3D.png", "/Ballon_GMV3D.png"])
    const spheresPerTexture = Math.floor(40 / textures.length)
    const remainder = 40 % textures.length

    return (
        <>
            {textures.map((texture, index) => {
                const count = spheresPerTexture + (index < remainder ? 1 : 0)
                return (
                    <TextureGroup key={index} texture={texture} count={count} />
                )
            })}
        </>
    )
}

function TextureGroup({ texture, count, mat = new THREE.Matrix4(), vec = new THREE.Vector3() }) {
    const [ref, api] = useSphere((index) => ({ args: [1], mass: 1, angularDamping: 0.1, linearDamping: 0.65, position: [rfs(20), rfs(20), rfs(20)] }))

    const material = useMemo(() => new THREE.MeshStandardMaterial({ roughness: 0, envMapIntensity: 0.5, map: texture }), [texture])

    const colors = useMemo(() => {
        const colorArray = new Float32Array(count * 3)
        for (let i = 0; i < count; i++) {
            const color = new THREE.Color().setHSL(Math.random(), 0.9, 0.5)
            colorArray[i * 3] = color.r
            colorArray[i * 3 + 1] = color.g
            colorArray[i * 3 + 2] = color.b
        }
        return colorArray
    }, [count])

    useFrame(() => {
        for (let i = 0; i < count; i++) {
            if (ref.current && api.at(i)) {
                ref.current.getMatrixAt(i, mat)
                api.at(i).applyForce(vec.setFromMatrixPosition(mat).normalize().multiplyScalar(-40).toArray(), [0, 0, 0])
            }
        }
    })

    return (
        <instancedMesh ref={ref} castShadow receiveShadow args={[sphereGeometry, material, count]}>
            <instancedBufferAttribute attach="instanceColor" args={[colors, 3]} />
        </instancedMesh>
    )
}

function Pointer() {
    const viewport = useThree((state) => state.viewport)
    const [ref, api] = useSphere(() => ({ type: "Kinematic", args: [3], position: [0, 0, 0] }))
    useFrame((state) => api.position.set((state.mouse.x * viewport.width) / 2, (state.mouse.y * viewport.height) / 2, 0))
    return (
        <mesh ref={ref} scale={0.2}>
            <sphereGeometry />
            <meshBasicMaterial color={[4, 4, 4]} toneMapped={false} />
            <pointLight intensity={40} distance={10} />
        </mesh>
    )
}
